import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeftIcon,
  UsersIcon,
  CheckCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { apiService } from "../services/api";
import { formatLocalDate, formatTime } from "../utils/helpers";
import { useAuth } from "../contexts/AuthContext";

const SessionCheckIn = () => {
  const { clubId, evalId, sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [assignedPlayers, setAssignedPlayers] = useState([]); // For workflow 2
  const [checkedInPlayers, setCheckedInPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerNumber, setPlayerNumber] = useState("");
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [playerLimitInfo, setPlayerLimitInfo] = useState(null); // { current, limit }
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [selectedPlayersForAssignment, setSelectedPlayersForAssignment] =
    useState([]);
  const [selectedSourceSession, setSelectedSourceSession] = useState("");

  // Modal filter states
  const [modalFilterText, setModalFilterText] = useState("");
  const [modalFilterGroup, setModalFilterGroup] = useState("");
  const [modalFilterGrade, setModalFilterGrade] = useState("");
  const [newPlayer, setNewPlayer] = useState({
    first_name: "",
    last_name: "",
    grade: "",
    grad_year: "",
    dob: "",
    group: "",
    position: "",
  });

  // Filter states
  const [filterText, setFilterText] = useState("");
  const [filterPosition, setFilterPosition] = useState("");
  const [filterGrade, setFilterGrade] = useState("");
  const [filterGradYear, setFilterGradYear] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [checkedInFilterText, setCheckedInFilterText] = useState("");

  const loadSessionData = useCallback(async () => {
    try {
      const [sessionData, playersData, checkInsData] = await Promise.all([
        apiService.getSession(sessionId),
        apiService.getEvaluationPlayers(evalId),
        apiService.getSessionCheckIns(sessionId),
      ]);

      setSession(sessionData);
      setAvailablePlayers(playersData);
      setCheckedInPlayers(checkInsData);

      // Load assigned players if this is an assigned mode session
      if (sessionData.assignment_mode === "assigned") {
        try {
          const assignedData =
            await apiService.getSessionAssignments(sessionId);
          setAssignedPlayers(assignedData);
        } catch (error) {
          console.error("Error loading assigned players:", error);
          // Continue without assigned players if there's an error
          setAssignedPlayers([]);
        }
      }

      // Start session if not already started
      if (sessionData.status !== "active" && evalId) {
        await apiService.updateSession(sessionId, {
          evaluation_id: evalId,
          status: "active",
        });
        setSession((prev) => ({ ...prev, status: "active" }));
      }
    } catch (error) {
      console.error("Error loading session data:", error);
      toast.error("Failed to load session data");
    }
    setLoading(false);
  }, [sessionId, evalId]);

  useEffect(() => {
    loadSessionData();
    apiService.getClubSubscriptionLimits(clubId)
      .then((limits) => setPlayerLimitInfo({ current: limits.players.current, limit: limits.players.limit }))
      .catch((err) => console.warn("Could not load subscription limits:", err));
  }, [loadSessionData, clubId]);

  const handleCheckInClick = (player) => {
    setSelectedPlayer(player);
    setPlayerNumber("");
    setShowCheckInModal(true);
  };

  const handleCheckIn = async () => {
    if (!selectedPlayer || !playerNumber) return;

    // Check if number is already taken
    if (
      checkedInPlayers.some(
        (ci) =>
          ci.evaluation_number.toLowerCase() === playerNumber.toLowerCase(),
      )
    ) {
      toast.error(`Player number ${playerNumber} is already assigned`);
      return;
    }

    try {
      const checkInData = {
        session_id: sessionId,
        player_id: selectedPlayer.id,
        evaluation_number: playerNumber,
      };

      const checkedInPlayer = await apiService.createCheckIn(checkInData);

      // Update local state
      setCheckedInPlayers((prev) => [...prev, checkedInPlayer]);

      toast.success(
        `${selectedPlayer.first_name} ${selectedPlayer.last_name} checked in with number ${playerNumber}`,
      );

      // Reset modal state
      setShowCheckInModal(false);
      setSelectedPlayer(null);
      setPlayerNumber("");
      setFilterText("");
    } catch (error) {
      console.error("Error checking in player:", error);
      toast.error("Failed to check in player");
    }
  };

  const handleRemoveCheckIn = async (checkIn) => {
    try {
      await apiService.removeCheckIn(sessionId, checkIn.player_id);

      // Update local state
      setCheckedInPlayers((prev) =>
        prev.filter((ci) => ci.player_id !== checkIn.player_id),
      );

      toast.success(
        `${checkIn.players.first_name} ${checkIn.players.last_name} check-in removed`,
      );
    } catch (error) {
      console.error("Error removing check-in:", error);
      toast.error("Failed to remove check-in");
    }
  };

  const handleAddPlayer = async () => {
    // Validate required fields
    if (!newPlayer.first_name || !newPlayer.last_name) {
      toast.error("First name and last name are required");
      return;
    }

    try {
      const playerData = {
        ...newPlayer,
        evaluation_id: evalId,
        grade: newPlayer.grade || null,
        grad_year: newPlayer.grad_year || null,
        dob: newPlayer.dob || null,
        group: newPlayer.group || null,
        position: newPlayer.position || null,
      };

      const addedPlayer = await apiService.createPlayer(playerData);

      // Update local state
      setAvailablePlayers((prev) => [...prev, addedPlayer]);

      toast.success(
        `${newPlayer.first_name} ${newPlayer.last_name} has been added to the evaluation`,
      );

      // Reset modal state
      setShowAddPlayerModal(false);
      setNewPlayer({
        first_name: "",
        last_name: "",
        grade: "",
        grad_year: "",
        dob: "",
        group: "",
        position: "",
      });
    } catch (error) {
      console.error("Error adding player:", error);
      toast.error(error.message || "Failed to add player");
    }
  };

  // Handle switching between assignment modes
  const handleAssignmentModeChange = async (newMode) => {
    try {
      await apiService.updateSession(sessionId, {
        evaluation_id: evalId,
        assignment_mode: newMode,
      });

      setSession((prev) => ({ ...prev, assignment_mode: newMode }));

      // If switching to assigned mode, load assignments
      if (newMode === "assigned") {
        const assignedData = await apiService.getSessionAssignments(sessionId);
        setAssignedPlayers(assignedData);
      }

      toast.success(
        `Switched to ${newMode === "assigned" ? "pre-assigned" : "open"} mode`,
      );
    } catch (error) {
      console.error("Error updating assignment mode:", error);
      toast.error("Failed to update assignment mode");
    }
  };

  // Handle showing assignment modal
  const handleShowAssignmentModal = async () => {
    try {
      // Load all evaluation sessions for copying option
      const evalSessions = await apiService.getEvaluationSessions(evalId);
      setAvailableSessions(evalSessions.filter((s) => s.id !== sessionId));
      setSelectedPlayersForAssignment([]);
      setModalFilterText("");
      setModalFilterGroup("");
      setModalFilterGrade("");
      setShowAssignmentModal(true);
    } catch (error) {
      console.error("Error loading sessions:", error);
      toast.error("Failed to load sessions");
    }
  };

  // Handle assigning selected players
  const handleAssignPlayers = async () => {
    if (selectedPlayersForAssignment.length === 0) {
      toast.error("Please select at least one player");
      return;
    }

    try {
      const assignments = await apiService.assignPlayersToSession(
        sessionId,
        selectedPlayersForAssignment,
      );

      setAssignedPlayers((prev) => [...prev, ...assignments]);
      setShowAssignmentModal(false);
      setSelectedPlayersForAssignment([]);

      toast.success(`${assignments.length} players assigned to session`);
    } catch (error) {
      console.error("Error assigning players:", error);
      toast.error("Failed to assign players");
    }
  };

  // Handle copying assignments from another session
  const handleCopyAssignments = async () => {
    if (!selectedSourceSession) {
      toast.error("Please select a source session");
      return;
    }

    try {
      const assignments = await apiService.copySessionAssignments(
        sessionId,
        selectedSourceSession,
      );

      setAssignedPlayers((prev) => [...prev, ...assignments]);
      setShowCopyModal(false);
      setSelectedSourceSession("");

      toast.success(`Copied ${assignments.length} player assignments`);
    } catch (error) {
      console.error("Error copying assignments:", error);
      toast.error("Failed to copy assignments");
    }
  };

  // Handle removing a player assignment
  const handleRemoveAssignment = async (playerId) => {
    try {
      await apiService.removePlayerAssignment(sessionId, playerId);

      setAssignedPlayers((prev) =>
        prev.filter((assignment) => assignment.player_id !== playerId),
      );

      toast.success("Player assignment removed");
    } catch (error) {
      console.error("Error removing assignment:", error);
      toast.error("Failed to remove assignment");
    }
  };

  const getFilteredPlayers = useMemo(() => {
    let playersToFilter;

    if (session?.assignment_mode === "assigned") {
      // In assigned mode, only show assigned players
      playersToFilter = assignedPlayers.map((assignment) => assignment.players);
    } else {
      // In open mode, show all available players
      playersToFilter = availablePlayers;
    }

    return playersToFilter
      .filter((player) => {
        // Don't show already checked-in players
        if (checkedInPlayers.some((ci) => ci.player_id === player.id)) {
          return false;
        }

        const fullName =
          `${player.first_name} ${player.last_name}`.toLowerCase();
        const matchesText =
          !filterText ||
          fullName.includes(filterText.toLowerCase()) ||
          (player.email &&
            player.email.toLowerCase().includes(filterText.toLowerCase()));

        const matchesPosition =
          !filterPosition ||
          (filterPosition === "__blank__"
            ? !player.position ||
              player.position === "" ||
              player.position === "-"
            : player.position === filterPosition);

        const matchesGrade =
          !filterGrade ||
          (filterGrade === "__blank__"
            ? !player.grade || player.grade === ""
            : player.grade?.toString() === filterGrade);

        const matchesGradYear =
          !filterGradYear ||
          (filterGradYear === "__blank__"
            ? !player.grad_year || player.grad_year === ""
            : player.grad_year?.toString() === filterGradYear);

        const matchesGroup =
          !filterGroup ||
          (filterGroup === "__blank__"
            ? !player.group || player.group === "" || player.group === "-"
            : player.group === filterGroup);

        return (
          matchesText &&
          matchesPosition &&
          matchesGrade &&
          matchesGradYear &&
          matchesGroup
        );
      })
      .sort((a, b) => a.last_name.localeCompare(b.last_name));
  }, [
    availablePlayers,
    assignedPlayers,
    checkedInPlayers,
    session?.assignment_mode,
    filterText,
    filterPosition,
    filterGrade,
    filterGradYear,
    filterGroup,
  ]);

  const getFilteredCheckedInPlayers = useMemo(() => {
    return checkedInPlayers
      .filter((checkIn) => {
        if (!checkedInFilterText) return true;

        const fullName =
          `${checkIn.players.first_name} ${checkIn.players.last_name}`.toLowerCase();
        return (
          fullName.includes(checkedInFilterText.toLowerCase()) ||
          checkIn.evaluation_number
            .toLowerCase()
            .includes(checkedInFilterText.toLowerCase())
        );
      })
      .sort((a, b) => a.players.last_name.localeCompare(b.players.last_name));
  }, [checkedInPlayers, checkedInFilterText]);

  const handleClearFilters = () => {
    setFilterText("");
    setFilterPosition("");
    setFilterGrade("");
    setFilterGradYear("");
    setFilterGroup("");
  };

  const getUniqueValues = useMemo(() => {
    return (field) => {
      const values = availablePlayers
        .map((player) => player[field])
        .filter((value) => value && value !== "")
        .filter((value, index, self) => self.indexOf(value) === index);
      return values.sort();
    };
  }, [availablePlayers]);

  // Filtered available players for assignment modal
  const getFilteredAvailablePlayersForAssignment = useMemo(() => {
    return availablePlayers
      .filter(
        (player) => !assignedPlayers.some((ap) => ap.player_id === player.id),
      )
      .filter((player) => {
        const fullName =
          `${player.first_name} ${player.last_name}`.toLowerCase();
        const matchesText =
          !modalFilterText || fullName.includes(modalFilterText.toLowerCase());

        const matchesGroup =
          !modalFilterGroup ||
          (modalFilterGroup === "__blank__"
            ? !player.group || player.group === "" || player.group === "-"
            : player.group === modalFilterGroup);

        const matchesGrade =
          !modalFilterGrade ||
          (modalFilterGrade === "__blank__"
            ? !player.grade || player.grade === ""
            : player.grade?.toString() === modalFilterGrade);

        return matchesText && matchesGroup && matchesGrade;
      })
      .sort((a, b) => a.last_name.localeCompare(b.last_name));
  }, [
    availablePlayers,
    assignedPlayers,
    modalFilterText,
    modalFilterGroup,
    modalFilterGrade,
  ]);

  // Select all functionality
  const handleSelectAll = () => {
    const allFilteredIds = getFilteredAvailablePlayersForAssignment.map(
      (player) => player.id,
    );
    setSelectedPlayersForAssignment(allFilteredIds);
  };

  const handleDeselectAll = () => {
    setSelectedPlayersForAssignment([]);
  };

  const areAllFilteredSelected = useMemo(() => {
    const filteredIds = getFilteredAvailablePlayersForAssignment.map(
      (player) => player.id,
    );
    return (
      filteredIds.length > 0 &&
      filteredIds.every((id) => selectedPlayersForAssignment.includes(id))
    );
  }, [getFilteredAvailablePlayersForAssignment, selectedPlayersForAssignment]);

  const areSomeFilteredSelected = useMemo(() => {
    const filteredIds = getFilteredAvailablePlayersForAssignment.map(
      (player) => player.id,
    );
    return filteredIds.some((id) => selectedPlayersForAssignment.includes(id));
  }, [getFilteredAvailablePlayersForAssignment, selectedPlayersForAssignment]);

  // Handle closing assignment modal with cleanup
  const handleCloseAssignmentModal = () => {
    setShowAssignmentModal(false);
    setSelectedPlayersForAssignment([]);
    setModalFilterText("");
    setModalFilterGroup("");
    setModalFilterGrade("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  const positions = getUniqueValues("position");
  const grades = getUniqueValues("grade");
  const gradYears = getUniqueValues("grad_year");
  const groups = getUniqueValues("group");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/portal/club/${clubId}/evaluation/${evalId}?tab=sessions`)
              }
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Evaluation
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              {session?.name} - Check In
            </h1>
            <Badge variant="green">Active</Badge>
          </div>
          <p className="text-gray-600 mt-1">
            {session?.session_date && formatLocalDate(session.session_date)}
            {session?.start_time && ` • ${formatTime(session.start_time)}`}
            {session?.location && ` • ${session.location}`}
          </p>
        </div>

        {/* Assignment Mode Toggle */}
        {user.role !== "coordinator" && (
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-700">Workflow:</span>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  session?.assignment_mode === "open"
                    ? "bg-white text-slate-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => handleAssignmentModeChange("open")}
              >
                Open Check-in
              </button>
              <button
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  session?.assignment_mode === "assigned"
                    ? "bg-white text-slate-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => handleAssignmentModeChange("assigned")}
              >
                Pre-assigned
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <UsersIcon className="h-8 w-8 text-slate-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">
                  {session?.assignment_mode === "assigned"
                    ? "Assigned Players"
                    : "Total Registered"}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {session?.assignment_mode === "assigned"
                    ? assignedPlayers.length
                    : availablePlayers.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Checked In</p>
                <p className="text-2xl font-bold text-gray-900">
                  {checkedInPlayers.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <UsersIcon className="h-8 w-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Remaining</p>
                <p className="text-2xl font-bold text-gray-900">
                  {getFilteredPlayers.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Available Players */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>
                {session?.assignment_mode === "assigned"
                  ? "Assigned Players"
                  : "Available Players"}{" "}
                ({getFilteredPlayers.length})
              </CardTitle>
              <div className="flex space-x-2">
                {session?.assignment_mode === "assigned" ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowCopyModal(true)}
                    >
                      Copy from Session
                    </Button>
                    <Button size="sm" onClick={handleShowAssignmentModal}>
                      Assign Players
                    </Button>
                  </>
                ) : (
                  (() => {
                    const limit = playerLimitInfo?.limit;
                    const current = playerLimitInfo?.current ?? availablePlayers.length;
                    const atLimit = limit != null && current >= limit;
                    return (
                      <div className="relative group">
                        <Button
                          size="sm"
                          onClick={atLimit ? undefined : () => setShowAddPlayerModal(true)}
                          disabled={atLimit}
                        >
                          Add Player
                        </Button>
                        {atLimit && (
                          <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                            {`Your plan allows ${limit} player${limit === 1 ? "" : "s"}. Upgrade your plan to add more.`}
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="mb-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Search Name"
                  placeholder="Search players..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                    value={filterPosition}
                    onChange={(e) => setFilterPosition(e.target.value)}
                  >
                    <option value="">All Positions</option>
                    <option value="__blank__">Blank</option>
                    {positions.map((position) => (
                      <option key={position} value={position}>
                        {position}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grade
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                    value={filterGrade}
                    onChange={(e) => setFilterGrade(e.target.value)}
                  >
                    <option value="">All Grades</option>
                    <option value="__blank__">Blank</option>
                    {grades.map((grade) => (
                      <option key={grade} value={grade}>
                        Grade {grade}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grad Year
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                    value={filterGradYear}
                    onChange={(e) => setFilterGradYear(e.target.value)}
                  >
                    <option value="">All Grad Years</option>
                    <option value="__blank__">Blank</option>
                    {gradYears.map((gradYear) => (
                      <option key={gradYear} value={gradYear}>
                        {gradYear}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                    value={filterGroup}
                    onChange={(e) => setFilterGroup(e.target.value)}
                  >
                    <option value="">All Groups</option>
                    <option value="__blank__">Blank</option>
                    {groups.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {(filterText ||
                filterPosition ||
                filterGrade ||
                filterGradYear ||
                filterGroup) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleClearFilters();
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Player List */}
            <div className="space-y-2 max-h-[460px] overflow-y-auto">
              {getFilteredPlayers.map((player) => (
                <div
                  key={player.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {player.first_name} {player.last_name}
                    </h4>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {player.position && <span>{player.position}</span>}
                      {player.grade && <span>Grade {player.grade}</span>}
                      {player.grad_year && <span>{player.grad_year}</span>}
                      {player.group && <span>Group {player.group}</span>}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      onClick={() => handleCheckInClick(player)}
                    >
                      Check In
                    </Button>
                    {session?.assignment_mode === "assigned" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemoveAssignment(player.id)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {getFilteredPlayers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  {session?.assignment_mode === "assigned"
                    ? assignedPlayers.length === 0
                      ? "No players assigned to this session yet. Use 'Assign Players' or 'Copy from Session' to add players."
                      : assignedPlayers.length === checkedInPlayers.length
                        ? "All assigned players have been checked in!"
                        : "No assigned players match the current filters."
                    : availablePlayers.length === checkedInPlayers.length
                      ? "All players have been checked in!"
                      : "No players match the current filters."}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Checked In Players */}
        <Card>
          <CardHeader>
            <CardTitle>
              Checked In Players ({getFilteredCheckedInPlayers.length}
              {checkedInFilterText && ` of ${checkedInPlayers.length}`})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Search Box for Checked In Players */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Checked In Players
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name or number..."
                  value={checkedInFilterText}
                  onChange={(e) => setCheckedInFilterText(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                />
                {checkedInFilterText && (
                  <button
                    onClick={() => setCheckedInFilterText("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-2 max-h-[550px] overflow-y-auto">
              {getFilteredCheckedInPlayers.map((checkIn) => (
                <div
                  key={checkIn.player_id}
                  className="flex items-center justify-between p-3 border border-green-200 bg-green-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm"></div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {checkIn.players.first_name} {checkIn.players.last_name}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{checkIn.evaluation_number}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRemoveCheckIn(checkIn)}
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              {getFilteredCheckedInPlayers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  {checkedInPlayers.length === 0
                    ? "No players checked in yet."
                    : "No checked-in players match the search."}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Check In Modal */}
      <Modal
        isOpen={showCheckInModal}
        onClose={() => {
          setShowCheckInModal(false);
          setSelectedPlayer(null);
          setPlayerNumber("");
        }}
        title="Check In Player"
      >
        {selectedPlayer && (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedPlayer.first_name} {selectedPlayer.last_name}
              </h3>
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-500 mt-1">
                {selectedPlayer.position && (
                  <span>{selectedPlayer.position}</span>
                )}
                {selectedPlayer.grade && (
                  <span>Grade {selectedPlayer.grade}</span>
                )}
                {selectedPlayer.grad_year && (
                  <span>'{selectedPlayer.grad_year}</span>
                )}
                {selectedPlayer.group && (
                  <span>Group {selectedPlayer.group}</span>
                )}
              </div>
            </div>

            <div>
              <Input
                label="Player Color and/or Number"
                placeholder="Enter player color and/or number..."
                value={playerNumber}
                onChange={(e) => setPlayerNumber(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && playerNumber) {
                    handleCheckIn();
                  }
                }}
                autoFocus
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowCheckInModal(false);
                  setSelectedPlayer(null);
                  setPlayerNumber("");
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleCheckIn} disabled={!playerNumber}>
                Check In
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Player Modal */}
      <Modal
        isOpen={showAddPlayerModal}
        onClose={() => {
          setShowAddPlayerModal(false);
          setNewPlayer({
            first_name: "",
            last_name: "",
            grade: "",
            grad_year: "",
            dob: "",
            group: "",
            position: "",
          });
        }}
        title="Add New Player"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="First Name *"
              placeholder="Enter first name..."
              value={newPlayer.first_name}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, first_name: e.target.value })
              }
              autoFocus
            />
            <Input
              label="Last Name *"
              placeholder="Enter last name..."
              value={newPlayer.last_name}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, last_name: e.target.value })
              }
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              label="Grade"
              placeholder="Enter grade..."
              value={newPlayer.grade}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, grade: e.target.value })
              }
            />
            <Input
              label="Grad Year"
              placeholder="Enter grad year..."
              value={newPlayer.grad_year}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, grad_year: e.target.value })
              }
            />
            <Input
              label="Position"
              placeholder="Enter position..."
              value={newPlayer.position}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, position: e.target.value })
              }
            />
            <Input
              label="Group"
              placeholder="Enter group..."
              value={newPlayer.group}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, group: e.target.value })
              }
            />
          </div>

          <div>
            <Input
              label="Date of Birth"
              type="date"
              value={newPlayer.dob}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, dob: e.target.value })
              }
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAddPlayerModal(false);
                setNewPlayer({
                  first_name: "",
                  last_name: "",
                  grade: "",
                  grad_year: "",
                  dob: "",
                  group: "",
                  position: "",
                });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddPlayer}
              disabled={!newPlayer.first_name || !newPlayer.last_name}
            >
              Add Player
            </Button>
          </div>
        </div>
      </Modal>

      {/* Player Assignment Modal */}
      <Modal
        isOpen={showAssignmentModal}
        onClose={handleCloseAssignmentModal}
        title="Assign Players to Session"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Select players to assign to this session:
          </p>

          {/* Filters */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <h4 className="text-sm font-medium text-gray-700">
              Filter Players
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Name
                </label>
                <input
                  type="text"
                  placeholder="Search players..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                  value={modalFilterText}
                  onChange={(e) => setModalFilterText(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                  value={modalFilterGroup}
                  onChange={(e) => setModalFilterGroup(e.target.value)}
                >
                  <option value="">All Groups</option>
                  <option value="__blank__">Blank</option>
                  {groups.map((group) => (
                    <option key={group} value={group}>
                      {group}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grade
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                  value={modalFilterGrade}
                  onChange={(e) => setModalFilterGrade(e.target.value)}
                >
                  <option value="">All Grades</option>
                  <option value="__blank__">Blank</option>
                  {grades.map((grade) => (
                    <option key={grade} value={grade}>
                      Grade {grade}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {(modalFilterText || modalFilterGroup || modalFilterGrade) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setModalFilterText("");
                  setModalFilterGroup("");
                  setModalFilterGrade("");
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Select All Controls */}
          {getFilteredAvailablePlayersForAssignment.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
                  checked={areAllFilteredSelected}
                  ref={(input) => {
                    if (input)
                      input.indeterminate =
                        areSomeFilteredSelected && !areAllFilteredSelected;
                  }}
                  onChange={() => {
                    if (areAllFilteredSelected) {
                      handleDeselectAll();
                    } else {
                      handleSelectAll();
                    }
                  }}
                />
                <span className="text-sm font-medium text-gray-700">
                  Select All Displayed (
                  {getFilteredAvailablePlayersForAssignment.length} players)
                </span>
              </div>

              <div className="text-sm text-gray-600">
                {selectedPlayersForAssignment.length} of{" "}
                {getFilteredAvailablePlayersForAssignment.length} selected
              </div>
            </div>
          )}

          {/* Player List */}
          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
            {getFilteredAvailablePlayersForAssignment.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {availablePlayers.filter(
                  (player) =>
                    !assignedPlayers.some((ap) => ap.player_id === player.id),
                ).length === 0
                  ? "All players are already assigned to this session."
                  : "No players match the current filters."}
              </div>
            ) : (
              getFilteredAvailablePlayersForAssignment.map((player) => (
                <label
                  key={player.id}
                  className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    className="mr-3 h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
                    checked={selectedPlayersForAssignment.includes(player.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPlayersForAssignment((prev) => [
                          ...prev,
                          player.id,
                        ]);
                      } else {
                        setSelectedPlayersForAssignment((prev) =>
                          prev.filter((id) => id !== player.id),
                        );
                      }
                    }}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {player.first_name} {player.last_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {[
                        player.position && `${player.position}`,
                        player.grade && `Grade ${player.grade}`,
                        player.group && `Group ${player.group}`,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={handleCloseAssignmentModal}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignPlayers}
              disabled={selectedPlayersForAssignment.length === 0}
            >
              Assign {selectedPlayersForAssignment.length} Player
              {selectedPlayersForAssignment.length !== 1 ? "s" : ""}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Copy Session Assignments Modal */}
      <Modal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        title="Copy Player Assignments"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Select a session to copy player assignments from:
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Source Session
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
              value={selectedSourceSession}
              onChange={(e) => setSelectedSourceSession(e.target.value)}
            >
              <option value="">Select a session...</option>
              {availableSessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.name} - {formatLocalDate(session.session_date)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCopyModal(false);
                setSelectedSourceSession("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCopyAssignments}
              disabled={!selectedSourceSession}
            >
              Copy Assignments
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SessionCheckIn;
